import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Strings } from '../../../../common/language/strings';
import { res } from '../../../../common/language/text/res';
import { UserModalComponent } from '../user-modal/user-modal.component';
import { BsModalRef, BsModalService, ModalDirective } from 'ngx-bootstrap/modal';
import { SecurityFacade } from '../../../../common/facade/SecurityFacade';
import { finalize } from 'rxjs/operators';
import { UserCredential } from '../../../../common/model/UserCredential';
import { IUserCredentialDto, UserCredentialDto } from '../../../../common/model/dto/UserCredentialDto';
import { forkJoin, Subscription } from 'rxjs';
import { AlertComponent } from 'ngx-bootstrap/alert';
import { UserRolesModalComponent } from '../user-roles-modal/user-roles-modal.component';
import { UserRole } from '../../../../common/model/UserRole';
import { SESSION_STORAGE, WebStorageService } from 'ngx-webstorage-service';
import { Permission } from '../../../../common/model/Permission';
import { Permissions } from '../../../../common/model/dictionary/Permissions';

@Component({
  selector: 'app-users-admin',
  templateUrl: './users-admin.component.html',
  styleUrls: ['./users-admin.component.css']
})
export class UsersAdminComponent implements OnInit, OnDestroy {
  // Labels and text
  public pageTitle: res;
  public addLabel: res;
  public editLabel: res;
  public searchLabel: res;
  public rolesLabel: res;

  public errorGettingData: res;

  // Table columns
  public accountLabel: res;
  public nameLabel: res;
  public emailLabel: res;
  public phoneLabel: res;
  public activeDirectoryLabel: res;
  public domainLabel: res;
  public descriptionLabel: res;
  public statusLabel: res;

  // Modal
  private userModalRef: BsModalRef;
  private _onHideModalSubscription: Subscription;
  private userRolesModalRef: BsModalRef;

  public loading: boolean;
  public messageLoader: string;
  public alerts: { msg: string, type: string }[];

  public rows: UserCredential[];
  public _filteredRows: UserCredential[];
  public selected: UserCredential[];

  public roles: UserRole[];


  public messagesTable: {
    emptyMessage: string,
    totalMessage: string
  };

  public canCreate: boolean;
  public canEdit: boolean;
  public canEditRoles: boolean;

  constructor(
    public strings: Strings,
    public modalService: BsModalService,
    private securityService: SecurityFacade,
    @Inject(SESSION_STORAGE) private _storage: WebStorageService,
  ) { 
    this.pageTitle = res.UsersAdmin;
    this.addLabel = res.Add;
    this.editLabel = res.Edit;
    this.searchLabel = res.Search;
    this.rolesLabel = res.Roles;

    this.errorGettingData = res.ErrorGettingData;

    this.accountLabel = res.Account;
    this.nameLabel = res.Name;
    this.emailLabel = res.Email;
    this.phoneLabel = res.Phone;
    this.activeDirectoryLabel = res.ActiveDirectory;
    this.domainLabel = res.Domain;
    this.descriptionLabel = res.Description;
    this.statusLabel = res.Status;

    this.rows = [];
    this._filteredRows = [];
    this.selected = [];

    this.roles = [];

    this.loading = false;
    this.messageLoader = '';
    this.alerts = [];

    this.messagesTable = {
      emptyMessage: this.strings.GetText(res.ThereIsNoInformation),
      totalMessage: "Total"
    }

    this.canCreate = false;
    this.canEdit = false;
    this.canEditRoles = false;
  }

  ngOnInit(): void {
    this.getUsersAndRoles();
    this.setPermissions();
  }

  ngOnDestroy(): void {
    if (this._onHideModalSubscription)
      this._onHideModalSubscription.unsubscribe();
  }

  private setPermissions(): void {
    const permissions: Permission[] = this._storage.get('permissions');

    this.canCreate = permissions.some(p => p.name === Permissions.AdminUsersCreate);
    this.canEdit = permissions.some(p => p.name === Permissions.AdminUsersEdit);
    this.canEditRoles = permissions.some(p => p.name === Permissions.AdminUsersEditRoles);
  }

  public getUsersAndRoles(): void {
    this.loading = true;
    forkJoin([
      this.securityService.GetUsersCredential(),
      this.securityService.GetRoles()
    ]).pipe(finalize(() => this.loading = false))
    .subscribe(([credentials, roles]) => {
      if (!credentials.completed || !roles.completed)
        return;

      this.rows = credentials.data;
      this._filteredRows = [...credentials.data];

      this.roles = roles.data;

    }, error => {
      this.addAlert(`<strong> ${this.strings.GetText(this.errorGettingData)} </strong>`, 'danger');
    });
  }

  public addUser(): void {
    this.userModalRef = this.modalService.show(UserModalComponent,
      { class: 'modal-lg' }
    );
    
    this.onCloseSubscription();
  }

  public editUser(): void {
    if (this.selected.length == 0)
      return;

    const user: UserCredentialDto = this.mapUserCredentialDto(this.selected[0]);

    this.userModalRef = this.modalService.show(UserModalComponent, {
      initialState: {
        user: user,
        _isEdit: true
      },
      class: 'modal-lg'
    });

    this.onCloseSubscription();
  }

  private onCloseSubscription(): void {
    this._onHideModalSubscription = this.userModalRef.content.onClose$.subscribe((response: number) => {
      if (response === 1) {
        this.getUsersAndRoles();
        this.addAlert(`<strong>${this.strings.GetText(res.UserSavedSuccessfully)}<strong>`, 'success');
        this.selected = [];
      } else if (response === -1) {
        this.addAlert(`<strong>${this.strings.GetText(res.UserSaveFailed)}<strong>`, 'danger');
        this.selected = [];
      }
    });
  }

  public updateFilter(event): void {
    const val = event.target.value.toLowerCase();

    const keys = Object.keys(this._filteredRows[0]);

    this.rows = this._filteredRows.filter(function (item) {
      for (let i = 0; i < keys.length; i++) {
        if (item[keys[i]] != null && (item[keys[i]].toString().toLowerCase().indexOf(val) !== -1 || !val)) {
            return true;
        }
      }
    });
  }

  public isSomeRowSelected(): boolean {
    return this.selected.length > 0;
  }

  private mapUserCredentialDto(user: UserCredential): UserCredentialDto{
    const userDto: UserCredentialDto = new UserCredentialDto();

    userDto.account = user.account;
    userDto.password = user.password;
    userDto.name = user.name;
    userDto.description = user.description;
    userDto.email = user.email;
    userDto.phone = user.phone;
    userDto.isActiveDirectory = user.isActiveDirectory;
    userDto.domain = user.domain;
    userDto.isAPIUser = user.isAPIUser;
    userDto.isCustomUser = user.isCustomUser;
    userDto.position = user.position;
    userDto.status = user.status;

    return userDto;
  }

  public addAlert(message: string, type: string): void {
    this.alerts = [];
    this.alerts.push({
      type: type,
      msg: message
    });
  }

  public onAlertClose(dismissedAlert: { msg: string, type: string }): void {
    this.alerts = this.alerts.filter(alert => alert !== dismissedAlert);
  }

  public showRolesModal(): void {
    if (this.selected.length == 0)
      return;

    this.userRolesModalRef = this.modalService.show(UserRolesModalComponent, {
      initialState: {
        user: this.selected[0],
        roles: this.roles
      },
      class: 'modal-lg'
    });

    this._onHideModalSubscription = this.userRolesModalRef.content.onClose$.subscribe((response: number) => {
      if (response === 1) {
        this.addAlert(`<strong>${this.strings.GetText(res.RolesUpdatedSuccessfully)}<strong>`, 'success');
      } else if (response === -1) {
        this.addAlert(`<strong>${this.strings.GetText(res.RolesUpdateFailed)}<strong>`, 'danger');
      }
    });
  }

}
